import { Dialog } from "construct-ui";
import NumberInputComponent from "../../components/number/number-input.component";
import codeMasterModel from "../../models/common/code-master.model";
import ViewBase from "../view-base.view";

/**
 * Common Code Add & Edit Dialog
 * 
 * Dialog used to add or edit codemaster details
 * 
 * @author Darrus Goh
 */
export default function CommonCodeAddEditDialog() {
    ViewBase.call(this);

    const CODEMASTER_GLOBAL_CODE = 'global';
    const FORM_ID = 'codemasterAddEditDialogForm';

    const DEFAULT_CODEMASTER_DETAILS = {
        code: '',
        name: '',
        description: '',
        sequence: '',
        status: false
    };

    let isEdit = false;

    let codemasterDetail = structuredClone(DEFAULT_CODEMASTER_DETAILS);
    let codemasterDetailBeforeEdit = structuredClone(codemasterDetail);

    const getTitle = (codemasterType, category) => {
        let title = codemasterType === CODEMASTER_GLOBAL_CODE ? this.getText('LT00000788', 'Global') : this.getText('', 'Country');
        title += ' - ';
        title += category.name;
        title += ' - ';
        title += isEdit ? this.getText('LT00000128', 'Edit') : this.getText('LT00000090', 'Add');
        title += ` ${this.getText('LT00000778', 'Code')}`;
        return title;
    }

    const getCodemasterDetail = (codemasterType, category, code) => {
        return codeMasterModel.getCodemasterDetail(codemasterType, category, code)
            .then(result => {
                codemasterDetail = structuredClone(result);
                codemasterDetailBeforeEdit = structuredClone(codemasterDetail);
            })
            .catch(error => {
                this.handleError(error);
            })
    }

    const updateCodemasterDetail = (onClose, codemasterDetail, codemasterType, category, code) => {
        return codeMasterModel.updateCodemasterDetail(codemasterType, category, code, codemasterDetail)
            .then(onClose)
            .catch(error => {
                this.handleError(error);
            });
    }

    const addCodemasterDetail = (onClose, codemasterDetail, codemasterType, category) => {
        return codeMasterModel.addCodemasterDetail(codemasterType, category, codemasterDetail)
            .then(onClose)
            .catch(error => {
                this.handleError(error);
            });
    }

    // Placed here because addCodemasterDetail and updateCodemasterDetails are variables and they have to exists before variable declaration
    const onSubmitFunctions = [addCodemasterDetail, updateCodemasterDetail];

    const hasChanges = () => {
        for (const key in codemasterDetailBeforeEdit) {
            if (codemasterDetailBeforeEdit[key] !== codemasterDetail[key]) {
                return true;
            }
        }
        return false;
    }

    const handleOnClose = (onClose) => () => {
        if (!hasChanges() || confirm(this.getText('EM00000776', 'Changes you made may not be saved'))) {
            onClose();
        }
    }

    return {
        oninit: ({ attrs: { codemasterType, codemasterCategory, codemasterDetailCode = '' } }) => {
            if (codemasterDetailCode !== '') {
                isEdit = true;
                getCodemasterDetail(codemasterType, codemasterCategory.code, codemasterDetailCode);
            }
        },
        view: ({ attrs: { codemasterType, codemasterCategory, codemasterDetailCode = '', onClose } }) => [
            m(Dialog, {
                title: getTitle(codemasterType, codemasterCategory),
                isOpen: true,
                onClose: onClose,
                closeOnEscapeKey: false,
                closeOnOutsideClick: false,
                content: m(`form#${FORM_ID}.form-control`, {
                    onsubmit: (e) => {
                        e.preventDefault();

                        if (hasChanges()) {
                            onSubmitFunctions[+isEdit](onClose, codemasterDetail, codemasterType, codemasterCategory.code, codemasterDetailCode);
                        } else {
                            this.showWarningMessage(this.getText('EM00000755', 'No changes were made.'), this.getText('LT00000737', 'No Changes'))
                        }
                    }
                },
                    m('.row.g-2',
                        m('.col-6',
                            m('label.form-label.mandatory', this.getText('LT00000778', 'Code')),
                            m('input.form-control', {
                                type: 'text',
                                required: true,
                                disabled: isEdit,
                                value: codemasterDetail?.code,
                                onchange: (element) => {
                                    codemasterDetail.code = element.target.value;
                                }
                            },)
                        ),
                        m('.col-6',
                            m('label.form-label.mandatory', this.getText('LT00000011', 'Name')),
                            m('input.form-control', {
                                type: 'text',
                                required: true,
                                value: codemasterDetail?.name,
                                onchange: (element) => {
                                    codemasterDetail.name = element.target.value;
                                }
                            })
                        ),
                        m('.col-7',
                            m('label.form-label', this.getText('LT00000012', 'Description')),
                            m('input.form-control', {
                                type: 'text',
                                value: codemasterDetail?.description,
                                onchange: (element) => {
                                    codemasterDetail.description = element.target.value;
                                }
                            })
                        ),
                        m('.col-3',
                            m('label.form-label.mandatory', this.getText('LT00000512', 'Sequence')),
                            m(NumberInputComponent, {
                                value: codemasterDetail?.sequence,
                                required: true,
                                onChange: (value) => {
                                    codemasterDetail.sequence = value;
                                }
                            })
                        ),
                        m('.col-2',
                            m('.vstack.h-100',
                                m('label.form-label', { style: { visibility: 'hidden' } }, 'M'), // Create a hidden label to simulate spacing of label so that checkbox is align with other inputs
                                m('.flex-fill.d-flex.justify-content-center.align-items-center',
                                    m('.form-check.d-inline-block',
                                        m('input.form-check-input', {
                                            id: 'codemasterDialogCheckbox',
                                            type: 'checkbox',
                                            checked: codemasterDetail?.status,
                                            onchange: (element) => {
                                                codemasterDetail.status = element.target.checked;
                                            }
                                        }),
                                        m('label.form-check-label', { for: 'codemasterDialogCheckbox' }, this.getText('LT00000025', 'Active'))
                                    )
                                )
                            )
                        )
                    )
                ),
                footer: m('.col-md-12.gap-2.d-flex.justify-content-center',
                    m('button.btn.btn-primary', { type: 'submit', form: FORM_ID },
                        isEdit ? this.getText('LT00000022', 'Save') : this.getText('LT00000090', 'Add')
                    ),
                    m('button.btn.btn-white', { type: 'button', onclick: handleOnClose(onClose) },
                        this.getText('LT00000021', 'Close')
                    )
                )
            }),
            this.getBaseViews()
        ]
    }
}